import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, Send, Paperclip, FileText } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import RichTextEditor from '../ui/RichTextEditor';
import PoweredByFooter from '../shared/PoweredByFooter';

const InvoiceEmailDialog = ({ open, onClose, invoiceId, invoiceNumber }) => {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    to: '',
    cc: '',
    bcc: '',
    display_name: '',
    reply_to: '',
    subject: '',
    body: '',
    from_email: 'mailbox@mygreendoorapp.com',
  });

  useEffect(() => {
    if (open && invoiceId) {
      fetchDraft();
    }
  }, [open, invoiceId]);

  const fetchDraft = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/invoices/${invoiceId}/email-draft`);
      setForm({
        to: res.data.to || '',
        cc: res.data.cc || '',
        bcc: res.data.bcc || '',
        display_name: res.data.display_name || '',
        reply_to: res.data.reply_to || '',
        subject: res.data.subject || '',
        body: res.data.body || '',
        from_email: res.data.from_email || 'mailbox@mygreendoorapp.com',
      });
    } catch (error) {
      console.error('Error fetching email draft:', error);
      toast.error('Failed to load email draft');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!form.to.trim()) {
      toast.error('Recipient email is required');
      return;
    }
    if (!form.subject.trim()) {
      toast.error('Subject is required');
      return;
    }
    setSending(true);
    try {
      await api.post(`/invoices/${invoiceId}/send-email`, {
        to: form.to,
        cc: form.cc,
        bcc: form.bcc,
        display_name: form.display_name,
        reply_to: form.reply_to,
        subject: form.subject,
        body: form.body,
      });
      toast.success('Invoice email sent successfully');
      onClose(true);
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const pdfFilename = `${invoiceNumber || 'invoice'}.pdf`;

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="invoice-email-dialog">
        <DialogHeader>
          <DialogTitle>Edit Email Message</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* To */}
            <div className="grid grid-cols-[80px_1fr] items-center gap-2">
              <Label className="font-semibold text-right">To</Label>
              <Input
                data-testid="email-to"
                value={form.to}
                onChange={(e) => setForm({ ...form, to: e.target.value })}
                placeholder="recipient@example.com"
              />
            </div>

            {/* CC */}
            <div className="grid grid-cols-[80px_1fr] items-center gap-2">
              <Label className="text-right text-muted-foreground">CC</Label>
              <Input
                data-testid="email-cc"
                value={form.cc}
                onChange={(e) => setForm({ ...form, cc: e.target.value })}
                placeholder="cc@example.com"
              />
            </div>

            {/* BCC */}
            <div className="grid grid-cols-[80px_1fr] items-center gap-2">
              <Label className="text-right text-muted-foreground">BCC</Label>
              <Input
                data-testid="email-bcc"
                value={form.bcc}
                onChange={(e) => setForm({ ...form, bcc: e.target.value })}
                placeholder="bcc@example.com"
              />
            </div>

            {/* From (read-only) + Display Name + Reply To */}
            <div className="border rounded-md p-3 space-y-3 bg-muted/20">
              <div className="grid grid-cols-[80px_1fr] items-center gap-2">
                <Label className="font-semibold text-right">From</Label>
                <span className="text-sm text-muted-foreground">{form.from_email}</span>
              </div>
              <div className="grid grid-cols-[80px_1fr] items-center gap-2 pl-4">
                <Label className="text-right text-sm text-muted-foreground">Display Name</Label>
                <Input
                  data-testid="email-display-name"
                  value={form.display_name}
                  onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                  placeholder="Your Name or Company"
                  className="h-8"
                />
              </div>
              <div className="grid grid-cols-[80px_1fr] items-center gap-2 pl-4">
                <Label className="text-right text-sm text-muted-foreground">Reply To</Label>
                <Input
                  data-testid="email-reply-to"
                  value={form.reply_to}
                  onChange={(e) => setForm({ ...form, reply_to: e.target.value })}
                  placeholder="reply@example.com"
                  className="h-8"
                />
              </div>
            </div>

            {/* Subject */}
            <div className="grid grid-cols-[80px_1fr] items-center gap-2">
              <Label className="font-semibold text-right">RE:</Label>
              <Input
                data-testid="email-subject"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="Email subject"
              />
            </div>

            {/* Body - Rich Text Editor */}
            <div>
              <RichTextEditor
                value={form.body}
                onChange={(value) => setForm({ ...form, body: value })}
                placeholder="Email content..."
              />
            </div>

            <PoweredByFooter />

            {/* Attachments */}
            <div className="border rounded-md overflow-hidden">
              <div className="bg-primary text-white px-4 py-2 text-sm font-semibold flex items-center gap-2">
                <Paperclip className="w-4 h-4" />
                Attachments
              </div>
              <div className="p-3">
                <div className="flex items-center gap-3 p-2 bg-muted/30 rounded">
                  <FileText className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium" data-testid="attachment-filename">{pdfFilename}</p>
                    <p className="text-xs text-muted-foreground">PDF Invoice (auto-generated)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)} disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || loading}
            className="bg-primary hover:bg-primary/90 text-white"
            data-testid="send-email-btn"
          >
            {sending ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
            ) : (
              <><Send className="w-4 h-4 mr-2" /> Send Email</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceEmailDialog;
